import { FC, useEffect, useMemo } from 'react'
import { Typography } from '@mui/material'
import { WHITE } from 'constants/styling/theme'

import { AIFeatureType } from '../../../common/useAIProgressMessages'
import { AiLabelOverlay } from '../AiLabel'
import { LabLiveOverlay } from '../lab/LabLiveOverlay.component'
import { PhotoStudioGenerating } from '../../../common/PhotoStudioLoading/PhotoStudioGenerating.component'
import { PhotoStudioSaving } from '../../../common/PhotoStudioSaving/PhotoStudioSaving.component'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useDynamicCursor } from './useDynamicCursor.hook'
import { useMaskDrawer } from './MaskDrawer.context'
import { useTranslation } from 'react-i18next'

export const DEFAULT_BRUSH_SIZE = 40

interface MaskDrawerProps {
  /** Whether the mask drawer is disabled */
  isDisabled?: boolean
}

/**
 * @example 
 * <MaskDrawer />
 */
export const MaskDrawer: FC<MaskDrawerProps> = ({ isDisabled = false }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const {
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
    handlePointerDown,
    handlePointerUp,
    handlePointerMove,
    brushSize,
    imageRef,
    previewImageMagicEraser,
    previewMaskCanvasRef,
    storedMaskCanvasRef,
    previewImageSizeRef,
    syncCanvasSize,
    isApplyingChanges,
    hasMaskDrawn,
    isProcessingImage,
    isSaving,
    handlePointerLeave,
    getCursorOverride,
    selectedObjectId,
    hoveredObject,
    objectsStatus,
  } = useMaskDrawer()

  const {
    imageUrl,
    isOriginalVisualType,
    aiProcessingStep,
    isLabSdkEngine,
    labProgress,
    labScene,
    showLabSceneImage,
  } = useClientPhotoStudio()

  // Lab SDK engine: Before / Compare are drawn by UnifiedPreview over a hidden drawer, so the drawer keeps the image
  // the mask belongs to — swapping its image would resize the canvas and wipe the mask
  const isShowingOriginal = isOriginalVisualType && !isLabSdkEngine

  const currentPreviewImage = useMemo(() => {
    return isShowingOriginal ? imageUrl : previewImageMagicEraser
  }, [isShowingOriginal, imageUrl, previewImageMagicEraser])

  // Only show loading if there's a mask to restore otherwise keep user's eyes free of drama
  const shouldShowLoading = isApplyingChanges && hasMaskDrawn()

  // Use the dynamic cursor hook
  useDynamicCursor({
    brushSize,
    canvasRef: previewMaskCanvasRef,
    isDisabled,
    // A pointer over a detected object (Lab SDK engine), the brush circle elsewhere
    getCursorOverride,
  })

  useEffect(() => {
    const img = imageRef.current
    if (!img) return

    const updateCanvasSize = () => {
      previewImageSizeRef.current = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      }
      syncCanvasSize()
    }

    if (img.complete) {
      updateCanvasSize()
    } else {
      img.onload = updateCanvasSize
    }

    // Use ResizeObserver to update canvas when image or container resizes
    const resizeObserver = new ResizeObserver(() => {
      // Small delay to ensure image has updated its size
      setTimeout(() => {
        syncCanvasSize()
      }, 0)
    })

    resizeObserver.observe(img)
    if (img.parentElement) {
      resizeObserver.observe(img.parentElement)
    }

    return () => {
      resizeObserver.disconnect()
    }
    // skip syncCanvasSize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewImageMagicEraser, imageRef, previewImageSizeRef])

  return (
    <div style={{ margin: 0, padding: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        style={{
          position: 'relative',
          display: 'inline-block',
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          overflow: 'hidden', // Clip GenerateLoading to image container boundaries
        }}
      >
        {/* Loaded image preview */}
        <img
          ref={imageRef}
          src={currentPreviewImage}
          alt="loaded_visual_for_edit_in_mask_drawer"
          style={{
            display: 'block',
            objectFit: 'contain', // Ensures the whole image is visible when portrait or landscape
            pointerEvents: 'none',
            margin: 'auto',
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            objectPosition: 'center',
          }}
        />

        {/* Canvas overlay for mask preview with opacity. Canvas size, position, and bounds will be set by syncCanvasSize. Hide this canvas when switching to original visual type. */}
        {!isShowingOriginal &&
          <canvas
            ref={previewMaskCanvasRef}
            id='previewMaskCanvas'
            style={{
              position: 'absolute',
              pointerEvents: isDisabled ? 'none' : 'auto',
              cursor: isDisabled ? 'not-allowed' : 'none',
              background: 'transparent', // Ensure canvas is transparent
              width: 'auto', // Prevent width inheritance, will be set by syncCanvasSize
              height: 'auto', // Prevent height inheritance, will be set by syncCanvasSize
              maxWidth: 'none', // Prevent max-width constraints, will be set by syncCanvasSize
              maxHeight: 'none', // Prevent max-height constraints, will be set by syncCanvasSize
              minWidth: '0', // Prevent min-width constraints, will be set by syncCanvasSize
            }}
            onMouseDown={isDisabled ? undefined : handleMouseDown}
            onMouseUp={isDisabled ? undefined : handleMouseUp}
            onMouseLeave={isDisabled ? undefined : handleMouseUp}
            onMouseMove={isDisabled ? undefined : handleMouseMove}
            onPointerDown={isDisabled ? undefined : handlePointerDown}
            onPointerUp={isDisabled ? undefined : handlePointerUp}
            onPointerLeave={isDisabled ? undefined : handlePointerLeave}
            onPointerMove={isDisabled ? undefined : handlePointerMove}
          />
        }

        {/* Live AI label preview above the mask canvas - burned into the image only on Save.
            The label intercepts pointer events over its own box (drag/resize beats brush there);
            drawing works everywhere else. */}
        {!isShowingOriginal && (
          <AiLabelOverlay imgRef={imageRef} />
        )}

        {/* Lab SDK engine: what a click does on the detected object under the pointer */}
        {hoveredObject && !isDisabled && !isShowingOriginal && (
          <Typography
            sx={{
              position: 'absolute',
              left: hoveredObject.x + 16,
              top: hoveredObject.y + 16,
              zIndex: 2,
              pointerEvents: 'none',
              padding: '0.4rem 0.8rem',
              borderRadius: '0.6rem',
              backgroundColor: 'rgba(17, 17, 17, 0.85)',
              color: WHITE,
              fontSize: '1.2rem',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
            }}
          >
            {t(hoveredObject.id === selectedObjectId ? 'lab.eraser.object_keep' : 'lab.eraser.object_erase')}
          </Typography>
        )}

        {/* Lab SDK engine: objects still being detected — the brush works meanwhile */}
        {objectsStatus === 'loading' && !isProcessingImage && !isShowingOriginal && (
          <Typography
            sx={{
              position: 'absolute',
              top: '1.2rem',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              pointerEvents: 'none',
              padding: '0.4rem 1.2rem',
              borderRadius: '10rem',
              backgroundColor: 'rgba(17, 17, 17, 0.75)',
              color: WHITE,
              fontSize: '1.2rem',
              lineHeight: 1.3,
            }}
          >
            {t('lab.eraser.detecting_objects')}
          </Typography>
        )}

        {/* Offscreen (hidden) canvas to store the actual mask, and just draw the visual preview with transparency on canvas overlay */}
        <canvas
          ref={storedMaskCanvasRef}
          id='storedMaskCanvas'
          style={{
            display: 'none', // Hidden canvas for mask storage
            position: 'absolute',
            pointerEvents: 'none',
            width: 'auto', // Prevent width inheritance, will be set by syncCanvasSize
            height: 'auto', // Prevent height inheritance, will be set by syncCanvasSize
            maxWidth: 'none', // Prevent max-width constraints, will be set by syncCanvasSize
            maxHeight: 'none', // Prevent max-height constraints, will be set by syncCanvasSize
            minWidth: '0', // Prevent min-width constraints, will be set by syncCanvasSize
            minHeight: '0', // Prevent min-height constraints, will be set by syncCanvasSize
            // Canvas size, position, and bounds will be set by syncCanvasSize
          }}
        />
        {/* Loading overlay - only over the canvas area and only when there's a mask to restore */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: shouldShowLoading ? 'blur(4px)' : 'blur(0px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            opacity: shouldShowLoading ? 1 : 0,
            visibility: shouldShowLoading ? 'visible' : 'hidden',
            transition: 'opacity 0.8s ease-in-out, visibility 0.8s ease-in-out, backdrop-filter 0.8s ease-in-out',
          }}
        />

        {/* PhotoStudioSaving overlay - clipped to image boundaries */}
        {isSaving && (
          <PhotoStudioSaving
            showBlurOverlay={true}
            previewImageUrl={previewImageMagicEraser}
          />
        )}
        {/* Generating overlay - clipped to image boundaries. Lab SDK engine: the photo and the mask stay clear, the run
            bar sits in the band above (UnifiedPreview) */}
        {isProcessingImage && !isSaving && isLabSdkEngine && (
          <LabLiveOverlay progress={labProgress} scene={labScene} previewImageUrl={previewImageMagicEraser} onSceneImage={showLabSceneImage} />
        )}
        {isProcessingImage && !isSaving && !isLabSdkEngine && (
          <PhotoStudioGenerating
            showText={true}
            showBlurOverlay={true}
            previewImageUrl={previewImageMagicEraser}
            featureType={AIFeatureType.MAGIC_ERASER}
            processingStep={aiProcessingStep}
            useDynamicMessages={true}
          />
        )}

      </div>
    </div>
  )
}
